import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Plugin, Space } from "types/generated";
import { AccessResult, hasAdminAccess, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

type ManageableWebhook = {
  plugin?: Pick<Plugin, "id" | "name"> | null;
  space: Pick<Space, "id">;
};

/**
 * Hook that checks if a user has access to manage a webhook.
 *
 * @returns A function that takes a webhook and returns whether the user has access to manage it
 */
const useCanManageWebhook = (): ((webhook?: ManageableWebhook | null) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (webhook?: ManageableWebhook | null) => {
      if (!webhook) {
        return { result: false };
      }

      if (webhook.plugin?.id) {
        return {
          result: false,
          message: `This context is managed by the plugin "${webhook.plugin.name}" and can't be edited manually.`,
        };
      }

      if (viewer.admin) {
        return { result: true };
      }

      const hasAccess = hasAdminAccess(viewer, webhook.space.id);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer]
  );
};

export default useCanManageWebhook;
